import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, HandCoins, FileUp, Clock, Scale } from "lucide-react";

const fmt = (n) => (n == null ? "-" : n.toLocaleString("tr-TR", { maximumFractionDigits: 2 }));

export default function PriceProposalPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [showCounter, setShowCounter] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");

  const load = () =>
    axios.get(`${API}/public/price-proposal/${token}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));

  useEffect(() => { load(); }, [token]);

  const respond = async (action) => {
    let counter = null;
    if (action === "counter") {
      counter = parseFloat(String(counterPrice).replace(/\./g, "").replace(",", "."));
      if (!counter || counter <= 0) return toast.error("Geçerli bir tutar girin");
    }
    setBusy(action);
    try {
      await axios.post(`${API}/public/price-proposal/${token}/respond`, { action, note, counter_price: counter });
      toast.success(action === "accept" ? "Yanıtınız iletildi — son onay sonrası fiyat güncellenecek" : action === "counter" ? "Karşı tutar öneriniz yönetim ekibine iletildi" : "Yanıtınız iletildi");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (error)
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6">
        <div className="border border-slate-200 bg-white p-10 text-center max-w-md" data-testid="proposal-error">
          <p className="font-heading text-xl font-bold">Bağlantı geçersiz</p>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  if (!data)
    return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center text-sm text-slate-500">Yükleniyor...</div>;

  const pending = data.status === "pending";
  const accepted = data.status === "supplier_accepted" || data.status === "applied";
  const countered = data.status === "supplier_countered";
  const isManual = data.origin === "manual";
  const isTarget = data.origin === "target";

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-slate-900 pb-20" data-testid="price-proposal-public-page">
      <header className="bg-white border-b border-slate-200 px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <span className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></span>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-0.5">Fiyat Önerisi</p>
          </div>
          {pending ? (
            <Badge variant="outline" className="rounded-none bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1.5 px-3 py-1.5"><Clock size={12} /> Yanıtınız bekleniyor</Badge>
          ) : countered ? (
            <Badge variant="outline" className="rounded-none bg-blue-500/10 text-blue-600 border-blue-500/20 gap-1.5 px-3 py-1.5"><Scale size={13} /> Karşı tutar iletildi</Badge>
          ) : accepted ? (
            <Badge variant="outline" className="rounded-none bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1.5 px-3 py-1.5"><CheckCircle2 size={13} /> Kabul edildi</Badge>
          ) : (
            <Badge variant="outline" className="rounded-none bg-red-500/10 text-red-500 border-red-500/20 gap-1.5 px-3 py-1.5"><XCircle size={13} /> Reddedildi</Badge>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 mt-10 space-y-8">
        <section>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">İhale</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" data-testid="proposal-request-title">{data.request_title}</h1>
          <p className="text-sm text-slate-500 mt-2">{data.company_name}</p>
        </section>

        <section className={`grid ${(data.previous_price != null ? 1 : 0) + (isTarget ? 0 : 1) >= 2 ? "sm:grid-cols-3" : (data.previous_price != null || !isTarget) ? "sm:grid-cols-2" : ""} gap-3`} data-testid="proposal-prices">
          {data.previous_price != null && (
            <div className="border border-slate-200 bg-white p-5" data-testid="proposal-previous-price">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Önceki Teklifiniz (aynı proje)</div>
              <div className="font-heading text-2xl font-extrabold tracking-tight mt-1">{fmt(data.previous_price)} <span className="text-sm font-medium text-slate-500">{data.previous_currency}</span></div>
            </div>
          )}
          {!isTarget && (
            <div className="border border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Mevcut Teklifiniz</div>
              <div className="font-heading text-2xl font-extrabold tracking-tight mt-1">{fmt(data.current_price)} <span className="text-sm font-medium text-slate-500">{data.currency}</span></div>
            </div>
          )}
          <div className="border border-amber-300 bg-amber-50/50 p-5">
            <div className="text-[10px] uppercase tracking-wider text-amber-600 flex items-center gap-1"><HandCoins size={11} /> {isTarget ? "Hedef Fiyat" : isManual ? "Komisyonca Belirtilen Tutar" : "Önerilen Fiyat"}</div>
            <div className="font-heading text-2xl font-extrabold tracking-tight mt-1 text-amber-700" data-testid="proposal-proposed-price">{fmt(data.proposed_price)} <span className="text-sm font-medium">{data.currency}</span></div>
          </div>
        </section>

        <section className="border border-slate-200 bg-white p-5" data-testid="proposal-message">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Mesaj</div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.message}</p>
        </section>

        <div className="border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm text-blue-800 flex items-start gap-2" data-testid="proposal-file-reminder">
          <FileUp size={15} className="shrink-0 mt-0.5" />
          <span>Malzeme detayları satır satır yazılmış olsa dahi, güncel <strong>teklif dosyalarınızı (PDF/Excel)</strong> tedarikçi panelinizden ilgili talebe mutlaka yüklemenizi rica ederiz.</span>
        </div>

        {pending ? (
          <section className="border border-slate-200 bg-white p-5 space-y-4" data-testid="proposal-respond">
            <p className="text-sm font-semibold text-slate-800">
              {isManual
                ? "İhale tutarı komisyon tarafından yukarıdaki şekilde belirtilmiştir. Kabul ediyor musunuz?"
                : "Önerilen tutarı kabul ediyor musunuz?"}
            </p>
            <div className="space-y-1.5">
              <Label>Not (opsiyonel)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Yanıtınıza eklemek istediğiniz not..." data-testid="proposal-note-input" className="rounded-none border-slate-200" />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => respond("accept")} disabled={!!busy} data-testid="proposal-accept-button" className="flex-1 rounded-none h-11 bg-emerald-600 hover:bg-emerald-500 text-white gap-2 font-semibold transition-colors duration-200">
                <CheckCircle2 size={15} /> {busy === "accept" ? "Gönderiliyor..." : "Evet, Kabul Ediyorum"}
              </Button>
              <Button variant="outline" onClick={() => respond("reject")} disabled={!!busy} data-testid="proposal-reject-button" className="flex-1 rounded-none h-11 border-red-500/30 text-red-500 hover:bg-red-500/10 gap-2 font-semibold transition-colors duration-200">
                <XCircle size={15} /> {busy === "reject" ? "Gönderiliyor..." : "Hayır, Reddediyorum"}
              </Button>
            </div>
            <div className="border-t border-slate-200 pt-4">
              {!showCounter ? (
                <button onClick={() => setShowCounter(true)} data-testid="proposal-show-counter" className="text-sm font-semibold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1.5 transition-colors duration-200">
                  <Scale size={14} /> Farklı bir tutar önermek istiyorum
                </button>
              ) : (
                <div className="space-y-2" data-testid="proposal-counter-section">
                  <Label>Karşı Tutar Önerim ({data.currency})</Label>
                  <div className="flex gap-2">
                    <Input type="text" inputMode="decimal" value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)} placeholder="Örn: 450.000" data-testid="proposal-counter-input" className="rounded-none border-slate-200 h-11 flex-1" />
                    <Button onClick={() => respond("counter")} disabled={!!busy} data-testid="proposal-counter-button" className="rounded-none h-11 px-5 bg-blue-600 hover:bg-blue-500 text-white gap-2 font-semibold transition-colors duration-200">
                      <Scale size={14} /> {busy === "counter" ? "Gönderiliyor..." : "Karşı Tutarı Gönder"}
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">Karşı tutarınız yönetim ekibinin onayına sunulur; kabul edilirse teklif fiyatınız bu tutara güncellenir.</p>
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-500">Kabul etmeniz durumunda tutar yönetim ekibinin son onayına sunulur; son onay sonrası teklif fiyatınız güncellenir.</p>
          </section>
        ) : (
          <section className={`border px-5 py-4 text-sm ${data.status === "superseded" ? "border-slate-300 bg-slate-50 text-slate-600" : countered ? "border-blue-500/30 bg-blue-500/5 text-blue-700" : data.status === "admin_rejected" ? "border-red-500/30 bg-red-500/5 text-red-600" : accepted ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700" : "border-red-500/30 bg-red-500/5 text-red-600"}`} data-testid="proposal-responded-banner">
            {data.status === "superseded"
              ? "Bu öneri yerine yeni bir fiyat önerisi gönderildi. Lütfen e-postanızdaki en güncel bağlantıyı kullanın."
              : countered
              ? `Karşı tutar öneriniz (${fmt(data.counter_price)} ${data.currency}) yönetim ekibine iletildi. Değerlendirme sonucu size e-postayla bildirilecektir.`
              : data.status === "admin_rejected"
              ? "İlettiğiniz tutar komisyon tarafından kabul edilmemiştir. Gerekli görülmesi halinde sizinle yeniden iletişime geçilecektir."
              : data.status === "applied"
              ? "Öneriyi kabul ettiniz ve yeni fiyat uygulandı. Teşekkür ederiz."
              : accepted
              ? "Öneriyi kabul ettiniz. Yönetim ekibinin son onayı sonrası teklif fiyatınız güncellenecektir. Lütfen güncel teklif dosyalarınızı panelden yüklemeyi unutmayın."
              : "Öneriyi reddettiniz. Yanıtınız yönetim ekibine iletildi."}
            {data.supplier_note && <div className="mt-1 text-xs opacity-80">Notunuz: {data.supplier_note}</div>}
          </section>
        )}
      </main>
    </div>
  );
}
